import { type FC } from 'react';
import { Form } from 'react-bootstrap';
import Papa from 'papaparse';
import * as uuid from 'uuid';

import {
	type TimelineDate,
	type TimelineSlide,
	TimelineDateFormat
} from '@editor2/types/pageElements';
import { loadDataBlock } from './styles';

/**
 * This is temporary solution for importing Timeline data as CSV file
 * as a part of Timeline transition from v1
 */

interface Props {
	onDataImport: (_) => void;
}

const ImportDataBlock: FC<Props> = (props) => {
	const { onDataImport } = props;

	const extractTimelineStartDate = (slide) => {
		const timeValues = slide['Time'].split(':');

		const startDate: TimelineDate = {
			...(slide['Year'] && { year: slide['Year'] }),
			...(slide['Month'] && { month: slide['Month'] }),
			...(slide['Day'] && { day: slide['Day'] }),
			...(slide['Time'] && { hour: timeValues[0] }),
			...(slide['Time'] && { minute: timeValues[1] || '' }),
			...(slide['Time'] && { second: timeValues[2] || '' })
		};

		return startDate;
	};

	const extractTimelineEndDate = (slide) => {
		const timeValues = slide['End Time'].split(':');

		const endDate: TimelineDate = {
			...(slide['End Year'] && { year: slide['End Year'] }),
			...(slide['End Month'] && { month: slide['End Month'] }),
			...(slide['End Day'] && { day: slide['End Day'] }),
			...(slide['End Time'] && { hour: timeValues[0] }),
			...(slide['End Time'] && { minute: timeValues[1] || '' }),
			...(slide['End Time'] && { second: timeValues[2] || '' })
		};

		return endDate;
	};

	const getDateFormatByDate = (date: TimelineDate) => {
		switch (true) {
			case !!date.hour:
				return TimelineDateFormat['Full date and time'];

			case !!date.day:
				return TimelineDateFormat['Full date'];

			case !!date.month:
				return TimelineDateFormat['Year and month'];

			case !!date.year:
				return TimelineDateFormat.Year;

			default:
				return null;
		}
	};

	const extractTimelineBackground = (slide) => {
		const backgroundString: string = slide['Background'];

		return backgroundString.startsWith('#')
			? { color: backgroundString }
			: { url: backgroundString };
	};

	const slideToTimelineSlide = (slide) => {
		const startDate = extractTimelineStartDate(slide);
		const endDate = extractTimelineEndDate(slide);

		const timelineSlide: TimelineSlide = {
			start_date: startDate,
			start_date_format: getDateFormatByDate(startDate),
			end_date: endDate,
			end_date_format: getDateFormatByDate(endDate),
			display_date: slide['Display Date'],
			group: slide['Group'],
			text: {
				headline: slide['Headline'],
				text: slide['Text']
			},
			media: {
				url: slide['Media'],
				thumbnail: slide['Media Thumbnail'],
				credit: slide['Media Credit'],
				caption: slide['Media Caption']
			},
			background: extractTimelineBackground(slide),
			unique_id: uuid.v4()
		};
		return timelineSlide;
	};

	const convertDataIntoTimeline = (data) => {
		const headers = data[0];
		const slides = data.slice(1).map((row: any[]) => {
			const slide = row.reduce((acc, curr, index) => {
				const propertyName = headers[index];
				acc[propertyName] = curr;
				return acc;
			}, {});

			return slide;
		});

		let titleSlide = null;
		const timelineSlides = [];
		slides.forEach((slide) => {
			const timelineSlide = slideToTimelineSlide(slide);
			if (slide['Type'] === 'title') titleSlide = timelineSlide;
			else timelineSlides.push(timelineSlide);
		});

		const timelineData = {
			...(titleSlide && { title: titleSlide }),
			events: timelineSlides
		};

		return timelineData;
	};

	const onImportFromCSVClick = (event) => {
		const file = event.target.files[0];

		Papa.parse(file, {
			skipEmptyLines: true,
			complete: (result) => {
				const timelineData = convertDataIntoTimeline(result.data);
				onDataImport(timelineData);
			}
		});
	};

	return (
		<div className={loadDataBlock}>
			<Form.Group>
				<Form.Label>Load data from csv file</Form.Label>
				<Form.Control type="file" onChange={(e) => onImportFromCSVClick(e)} />
			</Form.Group>
		</div>
	);
};

export default ImportDataBlock;
